import type {
  DefaultEventLocationType,
  EventLocationTypeFromApp,
  LocationObject,
} from "@calcom/app-store/locations";
import { getEventLocationType, getTranslatedLocation } from "@calcom/app-store/locations";
import { useIsPlatform } from "@calcom/atoms/hooks/useIsPlatform";
import { useBookerStoreContext } from "@calcom/features/bookings/Booker/BookerStoreProvider";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import invertLogoOnDark from "@calcom/lib/invertLogoOnDark";
import { useKnopkaEventTranslation } from "@calcom/web/modules/bookings/hooks/useKnopkaEventTranslation";
import classNames from "@calcom/ui/classNames";
import { Tooltip } from "@calcom/ui/components/tooltip";
import { LinkIcon, MapPinIcon } from "@coss/ui/icons";
import { shallow } from "zustand/shallow";
import { useMemo } from "react";

const excludeNullValues = (value: unknown) => !!value;

function RenderIcon({
  eventLocationType,
  isTooltip,
}: {
  eventLocationType: DefaultEventLocationType | EventLocationTypeFromApp;
  isTooltip: boolean;
}) {
  const isPlatform = useIsPlatform();

  return (
    <img
      src={`${isPlatform ? process.env.NEXT_PUBLIC_WEBAPP_URL : ""}${eventLocationType.iconUrl}`}
      className={classNames(invertLogoOnDark(eventLocationType?.iconUrl, isTooltip), "me-[10px] h-4 w-4")}
      alt={`${eventLocationType.label} icon`}
    />
  );
}

function resolveCustomLabel(
  customLabel: string | undefined,
  sourceLabels: string[],
  translatedLabels?: string[] | null
): string | undefined {
  const trimmed = customLabel?.trim();
  if (!trimmed) return undefined;
  const index = sourceLabels.indexOf(trimmed);
  if (index >= 0 && translatedLabels?.[index]) {
    return translatedLabels[index];
  }
  return trimmed;
}

function RenderLocationTooltip({
  locations,
  sourceLabels,
  translatedLabels,
}: {
  locations: LocationObject[];
  sourceLabels: string[];
  translatedLabels?: string[] | null;
}) {
  const { t } = useLocale();

  return (
    <div className="my-2 me-2 flex w-full flex-col stack-y-3 wrap-break-word">
      <p>{t("select_on_next_step")}</p>
      {locations.map(
        (
          location: Pick<Partial<LocationObject>, "link" | "address"> &
            Omit<LocationObject, "link" | "address">,
          index: number
        ) => {
          const eventLocationType = getEventLocationType(location.type);
          if (!eventLocationType) {
            return null;
          }
          const translatedLocation =
            resolveCustomLabel(location.customLabel, sourceLabels, translatedLabels) ||
            getTranslatedLocation(location, eventLocationType, t);
          return (
            <div key={`${location.type}-${index}`} className="font-sm flex flex-row items-center">
              <RenderIcon eventLocationType={eventLocationType} isTooltip />
              <p className="line-clamp-1">{translatedLocation}</p>
            </div>
          );
        }
      )}
    </div>
  );
}

export function AvailableEventLocations({
  locations,
  eventTitle,
  eventDescription,
  interfaceLanguage,
}: {
  locations: LocationObject[];
  eventTitle?: string | null;
  eventDescription?: string | null;
  interfaceLanguage?: string | null;
}) {
  const { t, i18n } = useLocale();
  const isPlatform = useIsPlatform();
  const [bookerUsername, bookerEventSlug, bookerEventId] = useBookerStoreContext(
    (state) => [state.username, state.eventSlug, state.eventId],
    shallow
  );

  const sourceLabels = useMemo(
    () => locations.map((location) => location.customLabel?.trim() || "").filter(Boolean),
    [locations]
  );

  const knopkaTranslation = useKnopkaEventTranslation({
    username: bookerUsername,
    eventSlug: bookerEventSlug,
    eventTypeId: bookerEventId,
    title: eventTitle,
    description: eventDescription,
    interfaceLanguage,
    locationLabels: sourceLabels,
    locale: i18n.language,
  });

  const renderLocations = locations.map(
    (
      location: Pick<Partial<LocationObject>, "link" | "address"> & Omit<LocationObject, "link" | "address">,
      index: number
    ) => {
      const eventLocationType = getEventLocationType(location.type);
      if (!eventLocationType) {
        return null;
      }

      const locationName =
        resolveCustomLabel(location.customLabel, sourceLabels, knopkaTranslation?.locationLabels) ||
        getTranslatedLocation(location, eventLocationType, t);

      return (
        <div key={`${location.type}-${index}`} className="flex flex-row items-center text-sm font-medium">
          {eventLocationType.iconUrl === "/link.svg" ? (
            <LinkIcon className="text-default h-4 w-4 ltr:mr-[10px] rtl:ml-[10px]" />
          ) : (
            <RenderIcon eventLocationType={eventLocationType} isTooltip={false} />
          )}
          <Tooltip content={locationName}>
            <p className="line-clamp-1">{locationName}</p>
          </Tooltip>
        </div>
      );
    }
  );

  const filteredLocations = renderLocations.filter(excludeNullValues) as JSX.Element[];

  return filteredLocations.length > 1 ? (
    <div className="flex flex-row items-center text-sm font-medium">
      {isPlatform ? (
        <MapPinIcon className={classNames("me-[10px] h-4 w-4 opacity-70 dark:invert")} />
      ) : (
        <img
          src="/map-pin-dark.svg"
          className={classNames("me-[10px] h-4 w-4 opacity-70 dark:invert")}
          alt="map-pin"
        />
      )}
      <Tooltip
        content={
          <RenderLocationTooltip
            locations={locations}
            sourceLabels={sourceLabels}
            translatedLabels={knopkaTranslation?.locationLabels}
          />
        }>
        <p className="line-clamp-1">
          {t("location_options", {
            locationCount: filteredLocations.length,
          })}
        </p>
      </Tooltip>
    </div>
  ) : filteredLocations.length === 1 ? (
    <div className="text-default mr-6 flex w-full flex-col stack-y-4 wrap-break-word text-sm rtl:mr-2">
      {filteredLocations}
    </div>
  ) : null;
}
